import { Injectable } from "@angular/core";
import { CollectApiServiceBase } from "./collect-api-base.service";
import { FormInstanceElement } from "../models/form-builder/form-instance-element.model";
import { HttpClient } from "@angular/common/http";
import { environment } from "../../../environments/environment";
import { CurrentUserService } from "../../security/current-user.service";
import { CurrentSiteService } from "./current-site.service";
import { FieldDefinitionService } from "./field-definition.service";
import { FormFieldService } from "./form-field.service";
import { FormFieldBaseComponent } from "../components/form-fields/form-field-base/form-field-base.component";
import { FormFieldProcessingPhaseEnum } from "../enums/form-field-processing-phase.enum";
import { BehaviorSubject, Subject } from "rxjs";
import { FieldValue } from "../components/form-fields/value-concatenator-form-field/extras";
import { Form } from "../models/form-builder/form.model";
import { FormInstance } from "../models/site-content/form-instance.model";

// See https://community-dc.max.gov/x/h4Gzmw for overview of how the Concatenator Field is implemented
@Injectable({
    providedIn: 'root'
})
export class ValueConcatenatorService extends CollectApiServiceBase<FormInstanceElement> {

    private lastUpdatedFieldValue: Subject<FieldValue> = new Subject<FieldValue>();
    // This is a BehaviorSubject  "Unlike a regular Subject, a BehaviorSubject holds the last emitted value and provides it immediately to new subscribers"
    private allFieldValuesOnLoad: BehaviorSubject<FieldValue[]> = new BehaviorSubject<FieldValue[]>([]);

    public constructor(http: HttpClient,
        private currentUserService: CurrentUserService,
        private currentSiteService: CurrentSiteService,
        private fieldDefinitionService: FieldDefinitionService,
        private formFieldService: FormFieldService
    ) {
        super(http, null, environment.apiUrl, 'fieldValueConcatenator', FormInstanceElement);

    }

    public updateEditedField(c: FormFieldBaseComponent) {
        // Concatenator fields only support single row Grids - any field updates in rows beyond the first one are ignored
        if (c.gridRowIndex == null || c.gridRowIndex == 0) {
            const fieldLogic = this.fieldDefinitionService.getFieldClientLogicHandler(c.formField.fieldDefinitionClassName);
            const displayValue = fieldLogic.getDisplayValue(c.formField, c.FormInstanceElement, null, FormFieldProcessingPhaseEnum.LOADING_DATA, null, false /*do not set default*/);
            this.lastUpdatedFieldValue.next(new FieldValue(c.FormField.id, c.FormField.name, c.FormField.displayName, displayValue));
        }
    }

    // Takes a FormInstance and updates this.allFieldValuesOnLoad with the values of all Form-level fields (not Grid-level fields)
    public updateAllFieldValues(formInstance: FormInstance, form: Form): void  {
        let list = [];
        for (let el of formInstance.formInstanceElements) {
            let field = form.formFields.find(x => { return x.id == el.formFieldId });
            if (field) {
                const fieldLogic = this.fieldDefinitionService.getFieldClientLogicHandler(field.fieldDefinitionClassName);
                if (fieldLogic.canParticipateInConcatenatorFieldBehavior) {
                    const val = fieldLogic.getDisplayValue(field, el, null, null, null, false /*do not set default*/);
                    const fieldValue = new FieldValue(field.id, field.name, field.displayName, val);
                    list.push(fieldValue);
                }
            }
        }

        // push on the formId meta data field also
        list.push(new FieldValue(-1, 'formId', 'FormId', `${formInstance.id}`));

        this.allFieldValuesOnLoad.next(list);
    }

    public updateEditedGridRow(fieldValues: FieldValue[]): void {
        for (const val of fieldValues) {
            this.lastUpdatedFieldValue.next(val);
        }
    }

    public get LastUpdatedFieldValue(): Subject<FieldValue> {
        return this.lastUpdatedFieldValue;
    }

    public get AllFieldValuesOnLoad(): Subject<FieldValue[]> {
        return this.allFieldValuesOnLoad;
    }
}
